import { AlertError, AlertPromise, AlertSuccess, AlertWarning } from '../componets/Alert/Alert';
import { Card, InfoBox, PageDescription } from '../style';
import ReactSelect, { SingleValue } from 'react-select';
import { cryptData, isAdmin, isServiceManager } from '../utils';
import { useContext, useEffect, useState } from 'react';

import { AccessLevel } from '../model/enum';
import { Btn } from '../componets/Buttons/Buttons';
import { Formik } from 'formik';
import { IServiceOfUser } from '../model';
import Loading from '../componets/Loading/Loading';
import { LoggedUserContext } from '../componets/UserContext/userContext';
import RoleHandler from '../componets/ServiceManagement/RoleHandler';
import { get } from 'lodash';
import { getUserServices } from '../api/fetch';
import httpClient from '../api/httpClient';
import { newServiceDefaults } from '../utils/constants';
import styled from 'styled-components';
import useSWRMutation from 'swr/mutation';
import { useTranslation } from 'react-i18next';

const ButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
`;
const MIN_DOWNLOAD_FEEDBACK_MS = 600;

const TwoColumnWrapper = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    margin-bottom: 1rem;
    max-width: 1200px;
    gap: 1rem;
    @media (min-width: 1200px) {
        flex-direction: row;
    }
`;

const InlineRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.5rem;
    & > div:first-child {
        flex: 1;
    }
`;

const ServiceManagement = () => {
    const { t } = useTranslation();
    const [newService, setNewService] = useState(false);
    const [selectedService, setSelectedService] = useState<IServiceOfUser | null>(null);
    const { trigger } = useSWRMutation('UpdateService', httpClient.postRequest);
    const { trigger: triggerUpdateServiceArea } = useSWRMutation('UpdateServiceArea', httpClient.postRequest);
    const { loggedUser } = useContext(LoggedUserContext);
    const [selectedServiceArea, setSelectedServiceArea] = useState<string>('');

    const isSm = isServiceManager(loggedUser?.accessLevel as AccessLevel);
    const isadmin = isAdmin(loggedUser?.accessLevel);

    const { isError, isLoading, services, mutate, isValidating } = getUserServices(isSm ? loggedUser?.userObjectId : undefined);

    useEffect(() => {
        setSelectedServiceArea(selectedService?.serviceAreaClientId ?? '');
    }, [selectedService]);

    const handleSelectChange = (id: string) => {
        //Dalla lista dei servizi correnti seleziona quello con l'id corrispondente e lo setta come selected in modo da poterlo visualizzare nel form di modifica
        const selected = services.serviceForUserList.find((service: IServiceOfUser) => service.id === id);
        setSelectedService(selected);
    };

    const handleDeleteService = async (id: string) => {
        try {
            const response = await httpClient.fetcher(`DeleteService?${cryptData(`serviceId=${id}`, true)}`);

            if (response) {
                AlertSuccess(t('generic.success'));
                setSelectedService(null);
                mutate();
            }
        } catch (error) {
            AlertError(t('generic.error'));
        }
    };
    const downloadUsers = async (id: string) => {
        const request = httpClient.fetcher(`ExportUsersForService?${cryptData(`serviceId=${id}`, true)}`);
        const minimumFeedbackTime = new Promise(resolve => window.setTimeout(resolve, MIN_DOWNLOAD_FEEDBACK_MS));
        const downloadPromise = Promise.all([request, minimumFeedbackTime]).then(([response]) => {
            if (response) {
                const blob = new Blob([response], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                const url = URL.createObjectURL(blob);
                link.setAttribute('href', url);
                link.setAttribute('download', `users_${selectedService?.name || 'export'}_${new Date().toISOString().split('T')[0]}.csv`);
                link.style.visibility = 'hidden';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }
        });

        await AlertPromise(t('btns.downloading'), downloadPromise, t('generic.success'), t('generic.error')).catch(() => undefined);
    };
    const serviceList = get(services, 'serviceForUserList', []);

    return (
        <>
            {isLoading && isValidating && <Card>{t('generic.loading')}</Card>}
            {isError && <Card>{t('errors.requestFailed')}</Card>}

            {!isLoading && !isError && !!serviceList && (
                <TwoColumnWrapper>
                    <Card>
                        <PageDescription>{t('routes.services')}</PageDescription>
                        {!newService && (
                            <TwoColumnWrapper>
                                <TwoColumnWrapper className='select-wrapper' style={{ paddingTop: '1rem' }}>
                                    <ReactSelect
                                        styles={{ container: base => ({ ...base, minWidth: '100%' }) }}
                                        placeholder={t('generic.selectOption')}
                                        onChange={(newValue: SingleValue<{ value: string; label: string; id: string }>) => {
                                            handleSelectChange(!!newValue ? newValue?.id : '');
                                        }}
                                        filterOption={(option, inputValue) => {
                                            if (!inputValue) return true;
                                            const q = inputValue.toLowerCase();
                                            return (
                                                (option.label ?? '').toLowerCase().includes(q) ||
                                                ((option.data as any).serviceArea ?? '').toLowerCase().includes(q) ||
                                                ((option.data as any).id ?? '').toLowerCase().includes(q) ||
                                                ((option.data as any).description ?? '').toLowerCase().includes(q)
                                            );
                                        }}
                                        options={serviceList.map((s: IServiceOfUser) => ({
                                            value: s.id,
                                            label: s.name,
                                            id: s.id,
                                            dataversId: s.dataverseId,
                                            serviceArea: s.serviceAreaClientId,
                                            description: s.description,
                                        }))}
                                    />
                                </TwoColumnWrapper>

                                {/* <Button
                                    style={{ maxWidth: '300px' }}
                                    type='button'
                                    id='button-3'
                                    onClick={() => {
                                        setNewService(prev => !prev);
                                        setSelectedService(null);
                                    }}>
                                    {t('generic.createService')}
                                </Button> */}
                            </TwoColumnWrapper>
                        )}

                        {/*SELECTED SERVICE FORM */}
                        {selectedService && (
                            <Formik
                                validateOnChange={false}
                                validateOnBlur={false}
                                enableReinitialize={true}
                                initialValues={selectedService}
                                onSubmit={async (values, { resetForm }) => {
                                    try {
                                        if (!values.dataverseId) {
                                            AlertWarning('Il DataverseId è obbligatorio');
                                            return;
                                        }
                                        const response = await trigger({
                                            ...selectedService,
                                            name: values.name,
                                            description: values.description,
                                            contactPerson: values.contactPerson,
                                            dataverseId: values.dataverseId,
                                        });

                                        if (response) {
                                            AlertSuccess(t('generic.success'));
                                            setSelectedService(null);
                                            resetForm();
                                            mutate();
                                        }
                                    } catch (error) {
                                        AlertError(t('generic.error'));
                                    }
                                }}>
                                {({ values, handleChange, handleSubmit }) => (
                                    <form onSubmit={handleSubmit}>
                                        <input type='text' className='form-control' value={values.id} name='id' placeholder='Id' readOnly onChange={handleChange} />
                                        <input type='text' className='form-control' value={values.name} name='name' placeholder='Nome' readOnly onChange={handleChange} />
                                        <input type='text' className='form-control' value={values.description} name='description' readOnly placeholder='Descrizione' onChange={handleChange} />
                                        <input type='text' className='form-control' value={values.contactPerson} name='reference' readOnly placeholder='Referenze' onChange={handleChange} />
                                        {/* <input type='text' className='form-control' value={values.dataverseId || ''} name='dataverseId' readOnly placeholder='DataverseId' onChange={handleChange} /> */}

                                        <InlineRow>
                                            <input
                                                type='text'
                                                className='form-control'
                                                placeholder='Service Area - clientID'
                                                value={selectedServiceArea}
                                                readOnly={!isadmin}
                                                onChange={e => setSelectedServiceArea(e.target.value)}
                                            />
                                            {isadmin && (
                                                <Btn
                                                    type='button'
                                                    disabled={selectedServiceArea === (selectedService?.serviceAreaClientId ?? '')}
                                                    onClick={async () => {
                                                        try {
                                                            const response = await triggerUpdateServiceArea({ serviceId: values.id, serviceAreaClientId: selectedServiceArea });
                                                            if (response) {
                                                                AlertSuccess(t('generic.success'));
                                                                mutate();
                                                            }
                                                        } catch {
                                                            AlertError(t('generic.error'));
                                                        }
                                                    }}
                                                    text={t('btns.edit')}
                                                />
                                            )}
                                        </InlineRow>

                                        <ButtonWrapper>
                                            <Btn type='button' id='button-3' onClick={() => downloadUsers(values.id)} text={t('generic.downloadUsers')} />
                                            {/* <Button type='submit' id='button-3'>
                                                {t('btns.edit')}
                                            </Button>
                                            <Button type='button' id='button-3' onClick={() => setSelectedService(null)}>
                                                {t('btns.cancel')}
                                            </Button>
                                            <Button type='button' id='button-3' onClick={() => handleDeleteService(values.id)}>
                                                {t('btns.delete')}
                                            </Button> */}
                                        </ButtonWrapper>
                                    </form>
                                )}
                            </Formik>
                        )}

                        {/*NEW SERVICE FORM */}
                        {newService && (
                            <Formik
                                validateOnChange={false}
                                validateOnBlur={false}
                                initialValues={newServiceDefaults}
                                onSubmit={async (values, { resetForm }) => {
                                    try {
                                        if (!values.dataverseId) {
                                            AlertWarning('Il DataverseId è obbligatorio');
                                            return;
                                        }
                                        const response = await trigger({
                                            id: newServiceDefaults.id,
                                            name: values.name,
                                            description: values.description,
                                            grantorId: loggedUser?.userObjectId ?? '',
                                            contactPerson: values.reference,
                                            contactPersonId: '00000000-0000-0000-0000-000000000000',
                                            roles: [],
                                            isVisible: true,
                                            startDate: null,
                                            endDate: null,
                                            dataverseId: values.dataverseId,
                                        });

                                        if (response) {
                                            AlertSuccess(t('generic.success'));
                                            resetForm();
                                            mutate();
                                            setNewService(false);
                                        } else {
                                            AlertWarning('Il DataverseId è obbligatorio');
                                        }
                                    } catch (error) {
                                        AlertError(t('generic.error'));
                                    }
                                }}>
                                {({ values, handleChange, handleSubmit }) => (
                                    <form onSubmit={handleSubmit}>
                                        <input type='text' className='form-control' value={values.id} name='id' placeholder='id' disabled onChange={handleChange} />
                                        <input type='text' className='form-control' name='name' placeholder='Nome' onChange={handleChange} />
                                        <input type='text' className='form-control' name='description' placeholder='Descrizione' onChange={handleChange} />
                                        <input type='text' className='form-control' name='reference' placeholder='Referenze' onChange={handleChange} />
                                        <input type='text' className='form-control' name='dataverseId' placeholder='DataverseId' onChange={handleChange} />

                                        <ButtonWrapper>
                                            <Btn type='submit' id='button-3' text={t('btns.create')} />
                                            <Btn type='button' id='button-3' onClick={() => setNewService(prev => !prev)} text={t('btns.cancel')} />
                                        </ButtonWrapper>
                                    </form>
                                )}
                            </Formik>
                        )}
                        {!newService && !selectedService && <InfoBox>{t('generic.inputServices')}</InfoBox>}
                    </Card>

                    <Card>
                        <PageDescription>{t('services.man')}</PageDescription>
                        {selectedService ? <RoleHandler selectedService={selectedService} setSelectedService={setSelectedService} /> : <InfoBox>{t('services.unselectedServ')}</InfoBox>}
                    </Card>
                </TwoColumnWrapper>
            )}
        </>
    );
};

export default ServiceManagement;
